import './visio-basket.component.ajscss';

import { IBasketStore } from '@agent/core/basket/basket.store';
import { BasketCategoryType, Sort } from '@agent/core/basket/models/basket';
import { BasketVisioLabels, Csr } from '@agent/core/channel-digit/visio/models/visio.requests.interfaces';
import { IUnitOutgoingCallDeprecatedService } from '@agent/core/phone/outcall/unit-outgoing-call-deprecated.service';
import { AppointmentStatus } from '@commons/models/appointment';
import { ChannelUtil } from '@commons/util/helper/channel';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, ILogService } from 'angular';
import { i18n } from 'i18next';
import { from, map } from 'rxjs';

import {
  AgentUserFacade,
  AppointmentFacade,
  ChanEnum,
  ChannelType,
  Chosen,
  deepCopy,
  LoaderDeprecatedService,
  MomentService,
  Profile,
  ProfileFacade,
  Skill,
  ToasterFacade,
} from '@contact/upgrade';

import { fromVisioBasket, VisioBasketAction } from '../';
import templateUrl from './visio-basket.component.ajs.html';
import { AppointmentType, RequestModel, VisioColumnModel, VisioLabelModel } from './visio-basket.model';

const MODULE_NAME = 'visioBasket';
const INVITATION_ID_SUFFIX = '-INVITE';

class VisioBasketCtrl implements IComponentController {
  public name = MODULE_NAME;

  public BasketCategoryType = BasketCategoryType;

  // store value
  public categorySelected: BasketCategoryType;
  public labelSelected: string;
  public isLoading: boolean;
  public currentPage: number;
  public pageSize: number;
  public availableSkills: Chosen<Skill>[];
  public requests: RequestModel[];
  public columns: VisioColumnModel[];
  public personalLabel: VisioLabelModel[];
  public nbCommonRequests: number;
  public nbPersonalRequests: number;
  public nbTotalCommonRequests: number;
  public nbTotalPersonalRequests: number;
  public nbRequests: number;
  public sort: Sort;
  public pullEnabled: boolean;

  // filters
  public contact: string;
  public participant: string;
  public agent: string;
  public skills: Skill[];
  public beginTime: Date;
  public endTime: Date;
  public isPeriodFilterEnabled: boolean;
  public appointmentType: AppointmentType | undefined;

  // internal
  public allSelected: boolean;
  public oneSelected: boolean;
  public filterState: boolean;
  public profile: Profile;

  constructor(
    private readonly $log: ILogService,
    private readonly $i18next: i18n,
    private readonly $uibModal: any,
    private readonly basketStore: IBasketStore,
    private readonly timeUtil: MomentService,
    private readonly toasterFacade: ToasterFacade,
    private readonly appointmentFacade: AppointmentFacade,
    private readonly loaderDeprecatedService: LoaderDeprecatedService,
    private readonly unitOutgoingCallDeprecatedService: IUnitOutgoingCallDeprecatedService,
    private readonly profileFacade: ProfileFacade,
    private readonly agentUserFacade: AgentUserFacade
  ) {}

  $onInit(): void {
    // category selected
    this.basketStore
      .pipe(fromVisioBasket.selectCategorySelected(), ng1Destroy(this))
      .subscribe(category => (this.categorySelected = category));
    // category selected
    this.basketStore.pipe(fromVisioBasket.selectLabelSelected(), ng1Destroy(this)).subscribe(label => (this.labelSelected = label));
    // loading
    this.basketStore.pipe(fromVisioBasket.selectCurrentLoading(), ng1Destroy(this)).subscribe(loading => (this.isLoading = loading));
    // current page
    this.basketStore.pipe(fromVisioBasket.selectCurrentPage(), ng1Destroy(this)).subscribe(currentPage => (this.currentPage = currentPage));
    // page size
    this.basketStore.pipe(fromVisioBasket.selectCurrentPageSize(), ng1Destroy(this)).subscribe(pageSize => (this.pageSize = pageSize));
    // available skills
    this.agentUserFacade.skills$
      .pipe(
        map(skills =>
          skills
            .filter(({ channel }) => ChannelUtil.equals(channel, ChanEnum.Out.Visio))
            .map(({ skill }) =>
              deepCopy({
                ...skill,
                chosen: false,
                selected: false,
                displayed: true,
              })
            )
        ),
        ng1Destroy(this)
      )
      .subscribe(skills => {
        this.availableSkills = skills;
        this.skills = [];
      });
    // requests
    let request$ = this.basketStore.pipe(fromVisioBasket.selectCurrentRequests<RequestModel>());
    request$.pipe(ng1Destroy(this)).subscribe(requests => (this.requests = requests));
    // all selected
    request$
      .pipe(
        map(requests => requests.length > 0 && requests.filter(request => request.selected).length === requests.length),
        ng1Destroy(this)
      )
      .subscribe(allSelected => (this.allSelected = allSelected));
    // one selected
    request$
      .pipe(
        map(requests => requests.some(request => request.selected)),
        ng1Destroy(this)
      )
      .subscribe(oneSelected => (this.oneSelected = oneSelected));

    // columns
    this.basketStore
      .pipe(fromVisioBasket.selectCurrentColumns<VisioColumnModel>(), ng1Destroy(this))
      .subscribe(columns => (this.columns = columns));
    // query filters
    this.basketStore.pipe(fromVisioBasket.selectQueryFilters(), ng1Destroy(this)).subscribe(filters => {
      this.agent = filters.agent;
      this.participant = filters.participant;
      this.contact = filters.contact;
      this.skills = filters.skills;
      this.beginTime = filters.beginTime;
      this.endTime = filters.endTime;
      this.isPeriodFilterEnabled = filters.isPeriodFilterEnabled;
      this.appointmentType = filters.appointmentType;
      this.availableSkills = this.availableSkills.map(availableSkill => {
        let isChosen = this.skills.some(skill => skill.id === availableSkill.id);
        return {
          ...availableSkill,
          chosen: isChosen,
          selected: isChosen,
        };
      });
    });
    // personal filters
    this.basketStore
      .pipe(fromVisioBasket.selectLabelByCategory<VisioLabelModel>(ChannelType.VISIO, BasketCategoryType.PERSONAL), ng1Destroy(this))
      .subscribe(filters => (this.personalLabel = filters));
    // nb common requests
    this.basketStore
      .pipe(fromVisioBasket.selectLabelCountByType(ChannelType.VISIO, BasketVisioLabels.ASSIGNABLE), ng1Destroy(this))
      .subscribe(count => (this.nbCommonRequests = count));
    this.basketStore
      .pipe(fromVisioBasket.selectLabelTotalCountByType(ChannelType.VISIO, BasketVisioLabels.ASSIGNABLE), ng1Destroy(this))
      .subscribe(count => (this.nbTotalCommonRequests = count));
    // nb personal requests
    this.basketStore
      .pipe(fromVisioBasket.selectLabelTotalCountByType(ChannelType.VISIO, BasketVisioLabels.ALL), ng1Destroy(this))
      .subscribe(count => (this.nbPersonalRequests = count));
    this.basketStore
      .pipe(fromVisioBasket.selectLabelTotalCountByType(ChannelType.VISIO, BasketVisioLabels.ALL), ng1Destroy(this))
      .subscribe(count => (this.nbTotalPersonalRequests = count));
    // nb current requests
    this.basketStore.pipe(fromVisioBasket.selectCurrentNbRequests(), ng1Destroy(this)).subscribe(count => (this.nbRequests = count));
    // current sort
    this.basketStore.pipe(fromVisioBasket.selectCurrentSort(), ng1Destroy(this)).subscribe(sort => (this.sort = sort));
    // current csr profile
    this.profileFacade.profile$.pipe(ng1Destroy(this)).subscribe(profile => (this.profile = profile));
  }

  public readableDateTimeWithPipe(dateToConvert: Date): string {
    return this.timeUtil.readableDateTimeWithPipe(dateToConvert);
  }

  public readableContact(request: RequestModel): string {
    let first = request.contactFirstName || '';
    let last = request.contactLastName || '';
    return first.toString().concat(' ', last.toString().toUpperCase());
  }

  public resetFilters(): void {
    this.basketStore.dispatch(new VisioBasketAction.ResetQueryFilter());
    this.refreshBasket();
  }

  public applyFilters(): void {
    this.basketStore.dispatch(
      new VisioBasketAction.ChangeQueryFilter({
        appointmentType: this.appointmentType,
        contact: this.contact,
        agent: this.agent,
        participant: this.participant,
        skills: this.skills.map(s => ({
          id: s.id,
          name: s.name,
        })),
        beginTime: this.beginTime,
        endTime: this.endTime,
        isPeriodFilterEnabled: this.isPeriodFilterEnabled,
      })
    );
    this.refreshBasket();
  }

  public changeCategory(category: BasketCategoryType): void {
    this.basketStore.dispatch(new VisioBasketAction.ChangeCategory(category));
  }

  public changePage(): void {
    this.basketStore.dispatch(new VisioBasketAction.ChangePage(this.currentPage));
  }

  public changeLabel({ label }: VisioLabelModel): void {
    if (label === this.labelSelected) {
      return;
    }
    this.basketStore.dispatch(new VisioBasketAction.ChangeLabel(label));
  }

  public refreshBasket(column?: string): void {
    this.basketStore.dispatch(new VisioBasketAction.ManualRefresh(column));
  }

  public changeSort(sortKey: string): void {
    if (!sortKey) {
      return;
    }
    this.refreshBasket(sortKey);
  }

  public getSkillNames(request: RequestModel): string {
    return request.skillList.map(skill => skill.name).join(', ');
  }

  public getAgent(csr: Csr): string {
    if (!!csr) {
      let first = csr.firstName || '';
      let last = csr.lastName || '';
      return first.toString().concat(' ', last.toString().toUpperCase());
    }
    return '';
  }

  public csrStatusAppointment(request: RequestModel): boolean {
    let result = false;
    if (request.refusingParticipants.length > 0) {
      request.refusingParticipants.forEach(refParticipant => {
        let csrId = '';
        this.profileFacade.id$.subscribe(csrUuid => (csrId = csrUuid));
        if (refParticipant.uuid === csrId) {
          result = true;
        }
      });
    }
    return result;
  }

  public getParticipant(request: RequestModel): string {
    if (request.participants.length > 0) {
      return request.participants.map(participant => this.getAgent(participant)).join(', ');
    } else if (request.awaitingParticipants.length > 0) {
      return this.$i18next.t('agent:contact.workspace.basket.visio.labels.awaiting');
    } else if (request.refusingParticipants.length > 0) {
      let result = this.$i18next.t('agent:contact.workspace.basket.visio.labels.none');
      request.refusingParticipants.forEach(refParticipant => {
        let csrId = '';
        this.profileFacade.id$.subscribe(csrUuid => (csrId = csrUuid));
        if (refParticipant.uuid === csrId) {
          result = this.$i18next.t('agent:contact.workspace.basket.visio.labels.unavailable');
        } else {
          result = this.$i18next.t('agent:contact.workspace.basket.visio.labels.awaiting');
        }
      });
      return result;
    } else {
      return this.$i18next.t('agent:contact.workspace.basket.visio.labels.none');
    }
  }

  public openRequest(request: RequestModel): void {
    this.basketStore.dispatch(new VisioBasketAction.OpenRequest(request));
  }

  public isCategory(category: BasketCategoryType): boolean {
    return this.categorySelected === category;
  }

  public isLabelEnabled({ label }: VisioLabelModel): boolean {
    return this.labelSelected === label;
  }

  public getAppointmentTypeLabel(appointmentType: AppointmentType): string {
    return appointmentType === AppointmentType.INVITE
      ? this.$i18next.t(`agent:contact.workspace.basket.visio.invitation`)
      : this.$i18next.t(`agent:contact.workspace.basket.visio.appointment`);
  }

  public getAppointmentStatusLabel(appointmentStatus: AppointmentStatus): string {
    switch (appointmentStatus) {
      case AppointmentStatus.REQUESTED:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.requested');
      case AppointmentStatus.REQUEST_CANCELLED:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.request_cancelled');
      case AppointmentStatus.CONFIRMED:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.confirmed');
      case AppointmentStatus.CANCELLED:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.cancelled');
      case AppointmentStatus.HONORED:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.honored');
      case AppointmentStatus.MISSED_BY_AGENT:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.missed_by_agent');
      case AppointmentStatus.MISSED_BY_CONTACT:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.missed_by_contact');
      case AppointmentStatus.ACCEPTED:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.accepted');
      case AppointmentStatus.AWAITING:
        return this.$i18next.t('agent:contact.workspace.basket.visio.labels.awaiting');
      default:
        return '-';
    }
  }

  public giveup(): void {
    this.$uibModal
      .open({
        component: 'popupGiveup',
        keyboard: false,
        ariaLabelledBy: 'modal-header',
        ariaDescribedBy: 'modal-body',
        resolve: {
          requests: () => this.requests.filter(request => request.selected),
        },
      })
      .result.then(() => this.refreshBasket());
  }

  public assign(): void {
    // active loader
    // list of container ids
    this.loaderDeprecatedService.active('Area-Basket');
    // inform agent process
    this.toasterFacade.notification({
      message: this.$i18next.t('agent:contact.workspace.basket.toast.assign.process'),
      duration: 2000,
    });
    from(this.requests.filter(r => r.selected)).subscribe();
  }

  /**
   * To tell if it's an invitation whose csr is the current agent, or it's an appointment whose awaiting participant contains the current agent,
   * if it is these two cases, we dont need to assign it with action, return false
   * TODO: if the elastic search has prevented these two cases, please delete this function and its corresponding template
   * @param request
   */
  public isActionNeeded(request: RequestModel): boolean {
    if (!!this.profile) {
      if (request.type === AppointmentType.INVITE) {
        return this.profile.id !== request.csr.uuid;
      } else {
        return request.awaitingParticipants.map(participant => participant.uuid).indexOf(this.profile.id) === -1;
      }
    } else {
      this.$log.error("[VisioBasketCtrl] Fail to load current agents's profile, please reload the page or reconnect!");
      return false;
    }
  }

  /**
   * To tell if a outgoing call request is already sent
   */
  get outCallRequested(): boolean {
    if (this.unitOutgoingCallDeprecatedService.unitOutgoingCall != null) {
      return this.unitOutgoingCallDeprecatedService.unitOutgoingCall.requested;
    }
    return false;
  }

  /**
   * To tell if the request for outgoing call is available
   * @param request
   */
  public canOpenOutCall(request: RequestModel): boolean {
    return (
      !!request.phoneNumber &&
      request.phoneNumber.length > 0 &&
      this.unitOutgoingCallDeprecatedService.canOpenOutcall() &&
      !this.unitOutgoingCallDeprecatedService.outcallDisabled()
    );
  }

  /**
   * The appointment in status of cancelled / request cancelled is not able to join
   * @param request
   */
  public isJoinable(request: RequestModel): boolean {
    return request.status !== AppointmentStatus.CANCELLED && request.status !== AppointmentStatus.REQUEST_CANCELLED;
  }

  /**
   * Only the REQUESTED or CONFIRMED but recycled appointment and AWAITING invitation need to be accepted
   * @param request
   */
  public isAcceptanceNeeded(request: RequestModel): boolean {
    if (request.type === AppointmentType.APPOINTMENT) {
      return request.status === AppointmentStatus.REQUESTED || (request.status === AppointmentStatus.CONFIRMED && request.recycled);
    } else {
      return request.status === AppointmentStatus.AWAITING;
    }
  }

  /**
   * Only the visio appointment receiver agent can confirm the visio appointment
   * If no agent is selected the default implementation works
   * @param request
   */
  public isCsrCompetentToConfirm(request: RequestModel): boolean {
    let csrId = '';
    this.profileFacade.id$.subscribe(csrUuid => (csrId = csrUuid));
    return request.csr && request.csr.uuid ? request.csr.uuid === csrId : true;
  }

  /**
   * To identify if the invitation is accepted
   * @param request
   */
  public isInvitationAccepted(request: RequestModel): boolean {
    return !!request.participants && request.participants.length > 0;
  }

  /**
   * Action: Send an outgoing call request
   * @param request
   */
  public callContact(request: RequestModel) {
    if (!this.outCallRequested) {
      if (!!request.phoneNumber && request.phoneNumber.length > 0) {
        this.unitOutgoingCallDeprecatedService.openUnitOutgoingCallForm(undefined, request.phoneNumber).then(() => {
          this.unitOutgoingCallDeprecatedService.finishAndClosePauseAndTask();
        });
      }
    }
  }

  /**
   * Action: Join the conference
   * @param request
   */
  public joinConference(request: RequestModel) {
    this.appointmentFacade.join(request.id, request.flowUuid);
  }

  /**
   * Action: Accept the appointment
   * @param request
   */
  public acceptAppointment(request: RequestModel): void {
    if (!!request.csr && !!request.csr.uuid && !request.recycled) {
      // when the appointment has its own target csr and it is not a recycled appointment, we confirm this appointment using its own csr
      this.appointmentFacade.acceptAppointment(
        request.id,
        request.csr.uuid,
        request.csr.login,
        request.csr.firstName,
        request.csr.lastName
      );
    } else {
      // in the opposite case, we should pass the current csr as the csr of the appointment
      if (!!this.profile) {
        this.appointmentFacade.acceptAppointment(
          request.id,
          this.profile.id,
          this.profile.login,
          this.profile.firstname,
          this.profile.lastname
        );
      }
    }
  }

  /**
   * Action: Accept the invitation
   * @param request
   */
  public acceptInvitation(request: RequestModel): void {
    if (!!this.profile) {
      this.appointmentFacade.acceptInvitation(this.invitationIdFilter(request.id), this.profile.id);
    }
    this.toasterFacade.notification({
      message: this.$i18next.t('agent:contact.workspace.basket.toast.assign.complete'),
      duration: 5000,
    });
  }

  /**
   * Action: Refuse the invitation
   * @param request
   */
  public refuseInvitation(request: RequestModel): void {
    if (!!this.profile) {
      this.appointmentFacade.refuseInvitation(this.invitationIdFilter(request.id), this.profile.id);
    }
    this.toasterFacade.notification({
      message: this.$i18next.t('agent:contact.workspace.basket.toast.giveUp.complete'),
      duration: 5000,
    });
  }

  private invitationIdFilter(invitationUuid: string): string {
    return invitationUuid.replace(INVITATION_ID_SUFFIX, '');
  }

  /**
   * Action: transfer an appointment to another csr
   * @param request
   */
  public openTransferPopin(request: RequestModel): void {
    this.appointmentFacade.openAppointmentTransferForm(request.id);
  }

  public inviteParticipant(request: RequestModel): void {
    if (request.id) {
      this.appointmentFacade.openAppointmentInvitationForm(request.id);
    }
  }
}

export class VisioBasket implements IComponentOptions {
  templateUrl = templateUrl;
  controller = [
    '$log',
    '$i18next',
    '$uibModal',
    'basketStore',
    'momentService',
    'toasterFacade',
    'appointmentFacade',
    'loaderDeprecatedService',
    'unitOutgoingCallDeprecatedService',
    'profileFacade',
    'agentUserFacade',
    VisioBasketCtrl,
  ];
  bindings = {} as { [binding: string]: string };
}

export default MODULE_NAME;
